declare global {
  interface Window {
    __KALON_EXPO_WEBVIEW__?: boolean;
    __VW_NATIVE_SHELL_EMBEDDED__?: boolean;
    __KALON_NATIVE_BRIEF__?: boolean;
    ReactNativeWebView?: {
      postMessage(message: string): void;
    };
  }
}

export const EXPO_WEBVIEW_WINDOW_FLAG = '__KALON_EXPO_WEBVIEW__';
const EMBEDDED_NATIVE_SHELL_WINDOW_FLAG = '__VW_NATIVE_SHELL_EMBEDDED__';
const NATIVE_BRIEF_SHELL_WINDOW_FLAG = '__KALON_NATIVE_BRIEF__';
export const NATIVE_TAB_SELECT_EVENT = 'kalon:native-tab-select';
export const NATIVE_DOCK_MESSAGE_SOURCE = 'kalonmoves-business-web';
export const NATIVE_PULL_REFRESH_EVENT = 'kalon:native-pull-refresh';
export const NATIVE_BRIEF_ACTION_EVENT = 'kalon:native-brief-action';

export type NativeBriefBridgeDestination =
  | { kind: 'route'; target: string }
  | { kind: 'event'; name: string };

export type NativeBriefBridgeFact = {
  id: string;
  icon: string;
  label: string;
  destination: NativeBriefBridgeDestination;
};

export type NativeBriefBridgePayload = {
  greeting: string;
  statement: string | null;
  facts: NativeBriefBridgeFact[];
  path: string;
  visible: boolean;
};

export type NativePullRefreshPhase = 'complete' | 'idle' | 'pulling' | 'refreshing';

export function isExpoWebViewHost() {
  if (typeof window === 'undefined') {
    return false;
  }

  return window[EXPO_WEBVIEW_WINDOW_FLAG] === true;
}

export function isEmbeddedNativeShell() {
  if (typeof window === 'undefined') {
    return false;
  }

  return window[EMBEDDED_NATIVE_SHELL_WINDOW_FLAG] === true;
}

/**
 * True when the embedded native shell advertises a native Daily Brief surface.
 * The web dashboard then skips its own in-page hero and only publishes data;
 * older shells lack the flag and keep rendering the web hero (graceful
 * degradation in both upgrade directions).
 */
export function isNativeDailyBriefEnabled() {
  return isEmbeddedNativeShell() && window[NATIVE_BRIEF_SHELL_WINDOW_FLAG] === true;
}

export function postNativePullRefreshState(phase: NativePullRefreshPhase, progress: number) {
  if (!isEmbeddedNativeShell() || !window.ReactNativeWebView) {
    return;
  }

  window.ReactNativeWebView.postMessage(
    JSON.stringify({
      source: NATIVE_DOCK_MESSAGE_SOURCE,
      version: 1,
      type: 'pull-refresh',
      payload: {
        path: window.location.pathname,
        phase,
        progress: Math.min(1, Math.max(0, progress)),
      },
    }),
  );
}

/**
 * Post the localized Daily Brief view model to the embedded native shell so it
 * can render it as a first-class React Native surface. No-ops outside the
 * shell (browser / PWA), so the web hero simply renders in place there.
 */
export function postNativeDailyBrief(payload: NativeBriefBridgePayload) {
  if (!isEmbeddedNativeShell() || !window.ReactNativeWebView) {
    return;
  }

  window.ReactNativeWebView.postMessage(
    JSON.stringify({
      source: NATIVE_DOCK_MESSAGE_SOURCE,
      version: 1,
      type: 'daily-brief',
      payload,
    }),
  );
}
